 /* packages/plan-catalog/buildPricingPresentationPayload.js */
 /* eslint max-len: ["error", { "code": 80 }] */

 /**
  * BUILD PRICING PRESENTATION PAYLOAD
  *
  * PURPOSE
  * -------
  * This helper merges:
  *   1) billing/source-of-truth plan config from plan-catalog/index.js
  *   2) presentation/content contract defaults from
  *      pricingPresentationContract.js
  *
  * It returns a merged, presentation-ready model that can later be used by:
  *   • scripts/generate-pricing-json.js
  *   • future pricing preview tools
  *   • future admin publish flows
  *   • future app-side pricing rendering
  *
  * IMPORTANT
  * ---------
  * This helper does NOT write files and does NOT talk to Firestore yet.
  * That is intentional.
  *
  * Phase 1 sequence:
  *   Step 1: define presentation contract defaults   ✅
  *   Step 2: build merge helper                      ← this file
  *   Step 3: update public JSON generator to use it
  *   Step 4: add Firestore content loading later
  *
  * DESIGN GOALS
  * ------------
  * 1. Avoid duplicating feature copy between cards and compare.
  * 2. Derive card features from reusable feature IDs.
  * 3. Derive compare rows from the same feature registry.
  * 4. Preserve billing-critical plan behavior from the existing catalog.
  * 5. Keep output stable and easy for WordPress to consume.
  *
  * OUTPUT SHAPE
  * ------------
  * {
  *   plans: [
  *     {
  *       key,
  *       name,
  *       kind,
  *       featured,
  *       pricingDisplay,
  *       availability,
  *       constraints,
  *       subheader,
  *       bestFor,
  *       featureIntro,
  *       features,
  *       pricing
  *     }
  *   ],
  *   compare: [
  *     {
  *       category,
  *       rows: [
  *         {
  *           featureId,
  *           label,
  *           tooltip,
  *           standard,
  *           pro,
  *           enterprise
  *         }
  *       ]
  *     }
  *   ]
  * }
  */

 const {
   getPlanConfigsForEnv,
 } = require("plan-catalog");

 const {
   PRICING_PRESENTATION_PLAN_KEYS,
   PRICING_PRESENTATION_DEFAULTS,
   normalizePricingPresentationDoc,
 } = require("./pricingPresentationContract");

 /* ──────────────────────────────────────────────────────────────
  * Helpers
  * ─────────────────────────────────────────────────────────── */

 function titleCasePlanKey(key) {
   const s = String(key || "").trim();
   if (!s) return "";
   return s.charAt(0).toUpperCase() + s.slice(1);
 }

 function derivePricingDisplay(plan) {
   if (plan?.pricingDisplay === "contact") return "contact";

   const monthly = plan?.pricing?.monthly?.perSeat ?? null;
   const annual = plan?.pricing?.annual?.perSeat ?? null;

   if (monthly == null && annual == null) return "contact";
   return "numeric";
 }

 function sortPlansByOrder(planEntries) {
   const desiredOrder = PRICING_PRESENTATION_PLAN_KEYS;

   return [...planEntries].sort((a, b) => {
     const ai = desiredOrder.indexOf(a.key);
     const bi = desiredOrder.indexOf(b.key);

     const aRank = ai === -1 ? Number.MAX_SAFE_INTEGER : ai;
     const bRank = bi === -1 ? Number.MAX_SAFE_INTEGER : bi;

     return aRank - bRank;
   });
 }

 function normalizeFeatureForCard(featureId, featureRecord) {
   return {
     id: featureId,
     name: featureRecord?.label || "",
     tooltip: featureRecord?.tooltip || null,
   };
 }

 function buildPlanPresentation(planKey, planCfg, presentationDoc) {
   const pricing = planCfg?.pricing || {};
   const pricingDisplay = derivePricingDisplay(planCfg);
   const featured = planKey === "pro";

   const planPresentation =
     presentationDoc?.planContent?.[planKey] ||
     PRICING_PRESENTATION_DEFAULTS.planContent[planKey] || {
       subtitle: "",
       bestFor: "",
       featureIntro: "Included features:",
       cardFeatureIds: [],
     };

   const allFeatures = presentationDoc?.features || {};
   const cardFeatureIds = Array.isArray(planPresentation.cardFeatureIds)
     ? planPresentation.cardFeatureIds
     : [];

   const features = cardFeatureIds
       .map((featureId) => {
         const record = allFeatures[featureId];
         if (!record) return null;
         return normalizeFeatureForCard(featureId, record);
       })
       .filter(Boolean);

   return {
     key: planKey,
     name: titleCasePlanKey(planKey),
     kind: planCfg?.kind || "base",
     featured,
     pricingDisplay,
     availability: {
       selfServe: planCfg?.availability?.selfServe !== false,
     },
     cta: planCfg?.cta || {},
     constraints: {
       minSeats:
         planCfg?.constraints?.minSeats != null ?
           planCfg.constraints.minSeats :
           null,
       maxSeats:
         planCfg?.constraints?.maxSeats != null ?
           planCfg.constraints.maxSeats :
           null,
       step:
         planCfg?.constraints?.step != null ?
           planCfg.constraints.step :
           1,
     },
     subheader: planPresentation.subtitle || "",
     bestFor: planPresentation.bestFor || "",
     featureIntro: planPresentation.featureIntro || "Included features:",
     features,
     pricing: {
       monthly: {
         flatFee: pricing?.monthly?.flatFee ?? 0,
         perSeat: pricing?.monthly?.perSeat ?? null,
       },
       annual: {
         flatFee: pricing?.annual?.flatFee ?? 0,
         perSeat: pricing?.annual?.perSeat ?? null,
       },
     },
   };
 }

 function buildCompareRowsForCategory(categoryKey, categoryLabel, features) {
   const rows = Object.entries(features)
       .filter(([, feature]) => feature?.categoryKey === categoryKey)
       .sort(([, a], [, b]) => {
         const ao = Number(a?.compareOrder ?? 0);
         const bo = Number(b?.compareOrder ?? 0);
         return ao - bo;
       })
       .map(([featureId, feature]) => ({
         featureId,
         label: feature?.label || "",
         tooltip: feature?.tooltip || null,
         standard: feature?.includedIn?.standard === true,
         pro: feature?.includedIn?.pro === true,
         enterprise: feature?.includedIn?.enterprise === true,
       }));

   return {
     category: categoryLabel,
     rows,
   };
 }

 function buildComparePresentation(presentationDoc) {
   const features = presentationDoc?.features || {};
   const categories = Array.isArray(presentationDoc?.compareCategories)
     ? presentationDoc.compareCategories
     : [];

   return categories
       .map((cat) =>
         buildCompareRowsForCategory(
             cat.key,
             cat.label,
             features,
         ))
       .filter((group) => Array.isArray(group.rows) && group.rows.length > 0);
 }

 /* ──────────────────────────────────────────────────────────────
  * Public builder
  * ─────────────────────────────────────────────────────────── */

 /**
  * Build merged pricing presentation payload for a billing environment.
  *
  * @param {Object} options
  * @param {string} options.billingEnv - prod|staging
  * @param {Object|null} options.presentationDocOverride - optional external doc
  * @returns {{plans:Array, compare:Array}}
  */
 function buildPricingPresentationPayload(options = {}) {
   const billingEnv = options.billingEnv || "prod";
   const presentationDocOverride = options.presentationDocOverride || null;

   const planConfigs = getPlanConfigsForEnv(billingEnv);

   /**
    * For phase 1 we normalize either:
    * - a provided presentation doc override (future Firestore source)
    * - or the in-code defaults
    */
   const normalizedPresentationDoc = normalizePricingPresentationDoc(
       presentationDocOverride || PRICING_PRESENTATION_DEFAULTS,
   );

   const plans = sortPlansByOrder(
       Object.entries(planConfigs)
           .filter(([, cfg]) => cfg?.kind === "base")
           .map(([planKey, planCfg]) =>
             buildPlanPresentation(
                 planKey,
                 planCfg,
                 normalizedPresentationDoc,
             ),
           ),
   );

console.log("[buildPricingPresentationPayload] compare debug", {
  compareCategories: normalizedPresentationDoc?.compareCategories,
  featureCategoryKeys: Object.entries(normalizedPresentationDoc?.features || {}).map(
    ([featureId, feature]) => ({
      featureId,
      categoryKey: feature?.categoryKey || null,
      label: feature?.label || null,
    }),
  ),
});

   const compare = buildComparePresentation(normalizedPresentationDoc);

   return {
     plans,
     compare,
   };
 }

 module.exports = {
   buildPricingPresentationPayload,
 };